import { cn } from 'lib/utils'
import { ComponentPropsWithoutRef, PropsWithChildren, ReactNode } from 'react'
import { TbExternalLink, TbWindowMaximize } from 'react-icons/tb'
import { NavLink, NavLinkProps } from 'react-router-dom'
import { sva, cx } from 'styled-system/css'
import { Badge, BadgeProps } from 'ui-kit/badge'
import { Link } from 'ui-kit/link'

import { JsonViewer } from '../JsonViewer'
import { Tooltip } from '../Tooltip'

const errorItemRecipe = sva({
  className: 'errorItem',
  slots: ['list', 'root', 'separator'],
  base: {
    root: {},
    list: {
      '& > .errorItem__separator:not(:first-child)': {
        display: 'block',
      },
    },
    separator: {},
  },
})

function List({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  const classes = errorItemRecipe()
  return (
    <div className={cn('flex flex-col gap-4', classes.list, className)} {...props}>
      {children}
    </div>
  )
}

function Root({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  const classes = errorItemRecipe()

  if (!children) return null

  return (
    <>
      <hr className={cn('border-t border-muted hidden', classes.separator)} />
      <div className={cn('flex flex-col gap-3 items-start', className)} {...props}>
        {children}
      </div>
    </>
  )
}

function Header({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={cn('flex flex-col gap-1 items-start', className)} {...props}>
      {children}
    </div>
  )
}

function BadgeItem({ children, className, ...props }: BadgeProps) {
  return (
    <Badge variant={'subtle'} className={cx('mb-1', className)} {...props}>
      {children}
    </Badge>
  )
}

function Title({ children, className, ...props }: ComponentPropsWithoutRef<'h2'>) {
  return (
    <h2 className={cn('font-semibold text-base leading-tight m-0 break-words', className)} {...props}>
      {children}
    </h2>
  )
}

function Description({ children, className, ...props }: ComponentPropsWithoutRef<'p'>) {
  return (
    <p className={cn('text-base text-muted-foreground m-0', className)} {...props}>
      {children}
    </p>
  )
}

const JSONPreviewRecipe = sva({
  className: 'errorItemJsonPreview',
  slots: ['root', 'button'],
  base: {
    root: {
      borderRadius: 'l2',
      paddingInline: 2,
      paddingBlock: 1,
      width: 'full',
      gap: 2,
    },
    button: {},
  },
  variants: {
    variant: {
      clickable: {
        root: {
          position: 'relative',
          _focusWithin: {
            outline: '2px solid',
          },
          _hover: {
            backgroundColor: 'bg.subtle',
          },
        },
        button: {
          position: 'absolute',
          inset: 0,
          zIndex: 'tooltip',
          opacity: 0,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
        },
      },
      viewer: {
        root: {
          width: 'full',
          overflowX: 'auto',
          paddingInlineStart: 5,
        },
      },
    },
  },
})

type JSONPreviewGenericProps = Omit<ComponentPropsWithoutRef<'div'>, 'onClick'> & {
  json: any
}
type JSONPreviewClickableProps = JSONPreviewGenericProps & {
  onClick: () => void
  tooltip: ReactNode
}
type JSONPreviewNonClickableProps = JSONPreviewGenericProps & {
  onClick?: never
  tooltip?: never
}
type JSONPreviewProps = JSONPreviewClickableProps | JSONPreviewNonClickableProps

function JSONPreview({ json, onClick, className, tooltip, ...props }: JSONPreviewProps) {
  const JSONPreviewRecipeClasses = JSONPreviewRecipe({
    variant: onClick ? 'clickable' : undefined,
  })
  const previewJson = JSON.stringify(json, null, '').slice(0, 65)

  return (
    <div
      className={cn(
        'flex flex-row gap-2 items-center text-secondary-foreground border border-border',
        JSONPreviewRecipeClasses.root,
        className,
      )}
      {...props}
    >
      {onClick && (
        <Tooltip tooltip={tooltip} delayDuration={200}>
          <button className={cn('block', JSONPreviewRecipeClasses.button)} onClick={onClick}>
            <span className='sr-only'>{tooltip}</span>
          </button>
        </Tooltip>
      )}
      <pre className='grow max-w-full truncate'>
        <code className='text-sm leading-8'>{previewJson}</code>
      </pre>
      {onClick && <TbWindowMaximize className='size-4' aria-hidden />}
    </div>
  )
}
function JSONViewer({ json, className, ...props }: ComponentPropsWithoutRef<'div'> & { json: any }) {
  const JSONPreviewRecipeClasses = JSONPreviewRecipe({ variant: 'viewer' })

  return (
    <div className={cn('flex flex-row gap-2 items-center', JSONPreviewRecipeClasses.root, className)} {...props}>
      <JsonViewer json={json} collapsed={false} />
    </div>
  )
}

function DocLink({ children, ...props }: PropsWithChildren<Omit<NavLinkProps, 'children'>>) {
  return (
    <Link
      asChild
      css={{ colorPalette: 'primary' }}
      className='inline-flex items-center text-secondary-foreground text-sm font-bold'
    >
      <NavLink {...props}>
        {children}
        <TbExternalLink className='size-4' aria-hidden />
      </NavLink>
    </Link>
  )
}

const ErrorItem = {
  List,
  Root,
  Header,
  Badge: BadgeItem,
  Title,
  Description,
  JSONPreview,
  JSONViewer,
  DocLink,
}

export { ErrorItem }
